<template>
  <v-form
    ref="form"
    v-model="valid"
    :disabled="isLoading"
    lazy-validation
    enctype="multipart/form-data"
    @submit.prevent="submitEvent"
  >
    <v-row :class="compact ? '' : 'mb-5 tw-pt-6'">
      <v-col v-if="!compact" cols="12" md="3" class="px-5 pt-5 mx-auto">
        <form-description>
          <template #title> {{ $t('admin.app.event_form_title') }} </template>
          <template #copy>
            {{ $t('admin.app.event_form_description') }}
          </template>
        </form-description>
      </v-col>
      <v-col
        :cols="12"
        :md="compact ? 12 : 9"
        :class="compact ? 'tw-space-y-3' : 'mx-auto pa-5 tw-space-y-6'"
      >
        <v-card :flat="compact" :class="cardClass">
          <v-container :class="compact ? 'pa-0' : 'pa-5'">
            <v-row>
              <v-col cols="12" sm="8" class="pb-0">
                <v-select
                  v-model="form.registry_form_id"
                  :label="$t('app.event')"
                  :disabled="!canEditRegistryForm"
                  :items="registriesSelect"
                  :rules="[rules.required]"
                  rounded
                  outlined
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card>
        <v-card
          v-if="hasFields"
          :flat="compact"
          :class="fieldsCardClass"
        >
          <v-container :class="compact ? 'pa-0' : 'tw-px-4 tw-py-6 sm:tw-p-6'">
            <div :class="compact ? 'tw-space-y-2' : 'tw-mt-6 tw-space-y-2'">
              <div v-if="hasBasicInfo" class="tw-grid tw-grid-cols-3">
                <div class="tw-col-span-2">
                  <v-text-field
                    ref="email"
                    v-model="form.email"
                    prepend-inner-icon="alternate_email"
                    :label="$t('forms.email')"
                    type="email"
                    outlined
                    validate-on-blur
                    :rules="[rules.required, rules.email]"
                    :loading="emailLookupLoading"
                    :hint="emailLookupHint"
                    persistent-hint
                    rounded
                    @blur="onEmailBlur"
                  />
                </div>
              </div>
              <div v-if="hasPassword" class="tw-grid tw-grid-cols-2 tw-gap-6">
                <div class="">
                  <v-text-field
                    v-model="form.password"
                    :label="$t('forms.password')"
                    :rules="[rules.required]"
                    outlined
                    :append-icon="
                      show_password ? 'visibility_off' : 'visibility'
                    "
                    :type="show_password ? 'text' : 'password'"
                    validate-on-blur
                    rounded
                    @click:append="show_password = !show_password"
                  />
                </div>
                <div>
                  <v-text-field
                    v-model="form.password_confirmation"
                    :label="$t('forms.confirm_password')"
                    :append-icon="
                      show_password_confirmation
                        ? 'visibility_off'
                        : 'visibility'
                    "
                    :type="show_password_confirmation ? 'text' : 'password'"
                    :rules="[rules.required, passwordMatch]"
                    outlined
                    validate-on-blur
                    required
                    rounded
                    @click:append="
                      show_password_confirmation = !show_password_confirmation
                    "
                  />
                </div>
              </div>
              <div v-if="hasBasicInfo" class="tw-grid tw-grid-cols-2 tw-gap-6">
                <div class="">
                  <v-text-field
                    v-model="form.first_name"
                    :label="$t('forms.first_name')"
                    :rules="[rules.required]"
                    :disabled="isExistingUser"
                    type="text"
                    outlined
                    validate-on-blur
                    required
                    rounded
                  />
                </div>
                <div>
                  <v-text-field
                    v-model="form.last_name"
                    :label="$t('forms.last_name')"
                    :rules="[rules.required]"
                    :disabled="isExistingUser"
                    type="text"
                    outlined
                    validate-on-blur
                    required
                    rounded
                  />
                </div>
              </div>
              <div class="tw-grid tw-grid-cols-2 tw-gap-x-6 tw-gap-y-2">
                <template v-for="default_field in form.default_fields">
                  <div v-if="default_field.is_active" :key="default_field.name">
                    <SelectCountries
                      v-if="default_field.name === 'country_code'"
                      v-model="form[default_field.name]"
                      :rules="[rules.required]"
                      outlined
                      rounded
                      validate-on-blur
                    />
                    <v-text-field
                      v-else
                      v-model="form[default_field.name]"
                      :label="default_field.label"
                      :name="default_field.name"
                      :rules="[rules.required]"
                      type="text"
                      outlined
                      validate-on-blur
                      rounded
                    />
                  </div>
                </template>
              </div>
              <div class="tw-grid tw-grid-cols-12 tw-gap-6">
                <div
                  v-for="custom_field in form.registry_form_submission"
                  :key="custom_field.uid"
                  class="tw-col-span-12"
                  :class="matchCols[custom_field.cols]"
                >
                  <template v-if="custom_field.field_type == 'input'">
                    <v-text-field
                      v-model="custom_field.model"
                      :label="custom_field.label"
                      :name="custom_field.name"
                      type="text"
                      outlined
                      validate-on-blur
                      required
                      rounded
                    />
                  </template>
                  <template v-if="custom_field.field_type == 'textarea'">
                    <v-textarea
                      v-model="custom_field.model"
                      :label="custom_field.label"
                      :name="custom_field.name"
                      outlined
                      validate-on-blur
                      required
                      rounded
                    />
                  </template>
                  <template v-if="custom_field.field_type == 'radio'">
                    <v-radio-group
                      v-model="custom_field.model"
                      class="tw-mt-0"
                      :label="custom_field.label"
                    >
                      <v-radio
                        v-for="option in custom_field.options"
                        :key="option.uid"
                        :label="option.text"
                        :value="option.value"
                      />
                    </v-radio-group>
                  </template>
                  <template v-if="custom_field.field_type == 'checkbox'">
                    <div>
                      <label
                        for="first_name"
                        class="tw-block tw-text-sm tw-font-medium tw-text-slate-900"
                      >
                        {{ custom_field.label }}
                      </label>
                      <v-checkbox
                        v-for="option in custom_field.options"
                        :key="option.uid"
                        v-model="custom_field.model_multiple"
                        class="tw-mt-0"
                        :label="option.text"
                        :value="option.value"
                      />
                    </div>
                  </template>
                  <template 
                    v-if="
                      custom_field.field_type == 'select' 
                      && custom_field.label != 'Departments' 
                      && custom_field.label != 'Provinces'
                      && custom_field.label != 'Districts'
                    "
                  >
                    <v-select
                      v-model="custom_field.model"
                      :label="custom_field.label"
                      outlined
                      rounded
                      :items="custom_field.options"
                    />
                  </template>
                  <template v-if="custom_field.field_type == 'number'">
                    <v-text-field
                      v-model="custom_field.model"
                      :label="custom_field.label"
                      :name="custom_field.name"
                      :rules="
                        custom_field.is_required ? [rules.required] : []
                      "
                      outlined
                      validate-on-blur
                      required
                      rounded
                      :maxlength="custom_field.characters"
                      counter
                      @keypress="onlyNumber"
                    />
                  </template>
                  <template v-if="custom_field.field_type == 'date'">
                    <v-text-field
                      v-model="custom_field.model"
                      :label="custom_field.label"
                      :name="custom_field.name"
                      :rules="
                        custom_field.is_required ? [rules.required] : []
                      "
                      type="date"
                      outlined
                      validate-on-blur
                      required
                      rounded
                    />
                  </template>
                  <template v-if="custom_field.field_type == 'select' && custom_field.label == 'Departments'">
                    <DepartmentsAutocomplete 
                      v-model="custom_field.model" 
                      :rules="custom_field.is_required ? [rules.required] : []"
                      @change="listProvinceByDepartments"/>
                  </template>
                  <template v-if="custom_field.field_type == 'select' && custom_field.label == 'Provinces'">
                    <ProvincesAutocomplete 
                      v-model="custom_field.model" 
                      :rules="custom_field.is_required ? [rules.required] : []"
                      :search="itemsProvinces" @change="listDistricByProvinces"/>
                  </template>
                  <template v-if="custom_field.field_type == 'select' && custom_field.label == 'Districts'">
                    <DistrictsAutocomplete 
                      v-model="custom_field.model" 
                      :rules="custom_field.is_required ? [rules.required] : []"
                      :search="itemsDistritcs" />
                  </template>
                </div>
              </div>
            </div>
          </v-container>
        </v-card>

        <v-card
          v-else
          :flat="compact"
          :class="fieldsCardClass"
        >
          <v-container class="tw-px-4 tw-py-6 sm:tw-p-6">
            <div class="tw-text-center">
              <h3 class="tw-mt-2 tw-text-base tw-font-medium tw-text-gray-900">
                {{ $t('admin.app.event_without_fields') }}
              </h3>
              <p class="tw-mt-1 tw-text-sm tw-text-gray-500">
                {{ $t('admin.app.event_without_fields_reminder') }}
              </p>
            </div>
          </v-container>
        </v-card>
      </v-col>
    </v-row>
    <template v-if="compact">
      <transition name="fade-transition" mode="out-in">
        <v-alert
          v-if="errorAlert"
          v-model="errorAlertProxy"
          dismissible
          color="red"
          border="left"
          elevation="2"
          colored-border
          icon="error"
          transition="fade-transition"
        >
          {{ errorMsg }}
        </v-alert>
      </transition>
    </template>
    <template v-else>
      <row-separator />
      <v-row class="mb-5">
        <v-col cols="12" md="9" class="px-0 py-0 ml-auto">
          <v-card
            class="mx-auto pa-5 tw-rounded-3xl tw-border-transparent tw-bg-transparent tw-shadow-none"
          >
            <transition name="fade-transition" mode="out-in">
              <v-row v-if="errorAlert">
                <v-col cols="12">
                  <v-alert
                    v-model="errorAlertProxy"
                    dismissible
                    color="red"
                    border="left"
                    elevation="2"
                    colored-border
                    icon="error"
                    transition="fade-transition"
                  >
                    {{ errorMsg }}
                  </v-alert>
                </v-col>
              </v-row>
            </transition>
            <v-card-actions>
              <v-row>
                <v-col cols="12" class="pa-0">
                  <div class="tw-flex tw-justify-end">
                    <AppBtn
                      color="primary"
                      :loading="isLoading"
                      icon="check"
                      type="submit"
                    >
                      {{ $t('app.save') }}
                    </AppBtn>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </v-form>
</template>

<script>
import AppBtn from '@/components/App/AppBtn.vue'
import FormDescription from '@/components/FormDescription.vue'
import { rules } from '@/utils/rules'
import { matchCols } from '@/utils/formCols'
import RowSeparator from '@/components/RowSeparator.vue'
import { mapGetters, mapState } from 'vuex'
import { LanguageValues } from '@/enums/LanguageKeys'
import SelectCountries from '@/components/FormInputs/SelectCountries.vue'
import { isObjectEmpty } from '@/utils/isObjectEmpty'
import { addModelText, onlyNumber } from '@/utils/Settings'
import DepartmentsAutocomplete from '@/components/DepartmentsAutocomplete'
import ProvincesAutocomplete from '@/components/ProvincesAutocomplete'
import DistrictsAutocomplete from '@/components/DistrictsAutocomplete'
import { SearchAutocompleteService } from '@/services/SearchAutocompleteService'
export default {
  components: {
    FormDescription,
    AppBtn,
    RowSeparator,
    SelectCountries,
    // Components Depa, Prov, Dist
    DepartmentsAutocomplete,
    ProvincesAutocomplete,
    DistrictsAutocomplete,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: false,
    },
    hasPassword: {
      type: Boolean,
      required: false,
    },
    showBasicInfo: {
      type: Boolean,
      required: false,
      default: false,
    },
    compact: {
      type: Boolean,
      required: false,
      default: false,
    },
    errorAlert: {
      type: Boolean,
      required: false,
    },
    canEditRegistryForm: {
      type: Boolean,
      required: false,
      default: true,
    },
    registryFormIds: {
      type: Array,
      required: false,
      default: () => [],
    },
    registryId: {
      type: Number,
      required: false,
      default: null,
    },
    errorMsg: {
      type: String,
      required: '',
      default: '',
    },
  },
  data: () => ({
    valid: true,
    rules,
    hasTerms: false,
    matchCols,
     // Selects Depa - Prov - Dist
    itemsProvinces: null,
    itemsDistritcs: null,
    onlyNumber,
    emailLookupLoading: false,
    emailLookupHint: '',
    isExistingUser: false,
  }),
  computed: {
    ...mapGetters({
      languages: ['constants/languages'],
    }),
    hasFields() {
      return (
        this.showBasicInfo ||
        this.form.default_fields.some((row) => row.is_active) ||
        this.form.registry_form_submission.some((row) => row.is_active)
      )
    },
    hasBasicInfo() {
      return this.showBasicInfo
    },
    cardClass() {
      return this.compact
        ? 'mx-auto tw-divide-y tw-divide-gray-200'
        : 'mx-auto tw-divide-y tw-divide-gray-200 tw-rounded-3xl tw-shadow-md'
    },
    fieldsCardClass() {
      return this.compact
        ? 'mx-auto sm:overflow-hidden'
        : 'mx-auto sm:overflow-hidden tw-rounded-3xl tw-shadow-md'
    },
    ...mapState({
      current_language_key: (state) => state.registry.show.current_language_key,
      registries: (state) => state.constants.data.registries,
      current_registry_uuid: (state) =>
        state.session.admin.loaded_registry.registry_uuid,
      current_form_id: (state) => state.session.admin.loaded_registry.form_id,
    }),
    currentForm() {
      if (!this.form.registry_form_id) {
        return {}
      }
      return this.registries
        .flatMap((row) => row.forms)
        .find((row) => row.id === this.form.registry_form_id)
    },
    registriesSelect() {
      return this.registries
        .filter((row) => !this.registryId || row.id === this.registryId)
        .flatMap((row) =>
          row.forms.map((form) => ({
            text: `${row.label} - ${this.$t(
              `admin.enums.${LanguageValues[form.language_id]}`,
            )}`,
            value: form.id,
          })),
        )
        .filter((row) => !this.registryFormIds.includes(row.value))
    },
    form: {
      get() {
        return new Proxy(this.value, {
          set: (obj, key, value) => {
            this.form = { ...obj, [key]: value }
            return true
          },
        })
      },
      set(newValue) {
        this.$emit('input', newValue)
      },
    },
    errorAlertProxy: {
      get() {
        return this.errorAlert
      },
      set(newValue) {
        this.$emit('update:errorAlert', newValue)
      },
    },
  },
  watch: {
    currentForm: {
      handler(newVal, oldVal) {
        console.log({ newVal })
        if (newVal.id !== oldVal.id) {
          this.initForm()
        }
      },
      deep: true,
    },
    'form.email'(newVal, oldVal) {
      if (newVal !== oldVal && this.isExistingUser) {
        this.form = { ...this.form, first_name: '', last_name: '' }
        this.isExistingUser = false
        this.emailLookupHint = ''
      }
    },
  },
  methods: {
    // TODO: Search
    listProvinceByDepartments(e) {
      this.itemsProvinces = e
    },
    listDistricByProvinces(e) {
      this.itemsDistritcs = e
    },
    // TODO: End Search
    initForm() {
      if (isObjectEmpty(JSON.parse(JSON.stringify(this.currentForm)))) {
        return
      }
      const current_form = JSON.parse(JSON.stringify(this.currentForm))
      const registry_form_submission = current_form.fields
      this.form = {
        ...this.form,
        registry_form_submission,
        default_fields: current_form.default_fields.filter(
          (row) => row.name !== 'email',
        ),
      }
      // .map((item) => {
      //   return { ...item, model: item.field_type === 'checkbox' ? [] : '' }
      // })

      // this.form.default_fields = current_form.default_fields.filter(
      //   (row) => row.name !== 'email',
      // )
    },
    passwordMatch() {
      return this.form.password === this.form.password_confirmation
        ? true
        : 'Contraseña no es igual'
    },
    onEmailBlur() {
      this.emailLookupHint = ''
      this.isExistingUser = false
      if (!this.showBasicInfo || rules.email(this.form.email) !== true) {
        return
      }
      this.emailLookupLoading = true
      SearchAutocompleteService.lookupUserByEmail(this.form.email)
        .then(({ data }) => {
          if (!data.exists) {
            return
          }
          this.form = {
            ...this.form,
            first_name: data.first_name || this.form.first_name,
            last_name: data.last_name || this.form.last_name,
          }
          this.isExistingUser = true
          this.emailLookupHint = this.$t('admin.app.attendee_email_found')
        })
        .catch(() => {})
        .finally(() => {
          this.emailLookupLoading = false
        })
    },
    submitEvent() {
      this.errorAlertProxy = false
      if (!this.$refs.form.validate()) {
        return
      }
      this.$emit('submit-form', this.form = {
        ...this.form,
        registry_form_submission: addModelText(this.form.registry_form_submission),
      })
    },
  },
}
</script>

<style></style>
